/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Butikk — the orders members pay for in the app, and the mosque's delivery options (store checkout, owner 2026-10-18;
 * the server side is apps/api/src/store-orders.js, its routes in server.js «Store checkout, staff side»).
 *
 * Vipps only RESERVES the money at checkout. Staff TAKE it by marking the order «Utlevert» (picked up) or «Sendt»
 * (shipped); they can release a reservation («Kanseller og frigi») or give money back («Refunder»). Every one of those
 * asks first, saying what will happen to the money, and is sent with a request id that stays the same for a double
 * click or a «Prøv igjen» (lib/store-orders Attempts): the server then answers with the same operation, never a second.
 *
 * The buttons follow the server's `can` — never a rule of the panel's own. An approved order not handed over is
 * released by the server after 6 days; staff see it marked from day 4.
 */
import React, { useEffect, useRef, useState } from 'react';
import { download, errText, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { osloTime } from '../lib/format';
import { Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Shipping, type Carrier, type ShippingRate } from '../core';
import {
  Attempts, AUTO_RELEASE_DAYS, CARRIER_NO, DELIVERY_NO, NOTICE_NO, ORDER_FILTERS, PAY_NO, SELLER_NO, SHIPPING_MODE_NO, handoverDue, inFilter, krText, money, operationWord,
  outcomeText, parseKr, parseRates, rateRow, ratesDirty, sellerDirty, sellerFields, shipOutcomeText, statusWord, weightText, type OpKind, type RateRow, type SellerFields,
} from '../lib/store-orders';

/* ------------------------------------------------------------------ is checkout on? */

/** Who sells, as the buyer is told before paying (audit F-04). Server: core store-checkout seller(). */
export type Seller = { name: string | null; orgNo: string | null; address: string | null; email: string | null; phone: string | null };
/**
 * `sellerMissing`: the required seller details not written yet — while any is, members cannot order (server).
 * Shop shipping (2026-10-31): `shippingMode` flat (one fee) or carrier (the rate table `rates`, per carrier and weight),
 * `shippingCarriers` the ones the mosque uses, `labels` whether the platform's shipping account can make labels.
 */
export type DeliverySettings = { pickup: boolean; shipping: boolean; shippingFeeOre: number; pickupNote: string | null; seller?: Seller; sellerMissing?: string[];
  shippingMode?: 'flat' | 'carrier'; shippingCarriers?: Carrier[]; labels?: boolean; rates?: ShippingRate[] };
/** A shipped order's shipping, as the server shows it (store-orders staffOrder). */
type OrderShipping = { carrier: Carrier | null; trackingNumber: string | null; trackingUrl: string | null; shippedAt: string | null; shippingMethod: string | null;
  pickupPoint: string | null; label: boolean; weightG: number };
export type Checkout = { state: 'loading' } | { state: 'off' } | { state: 'error'; message: string } | { state: 'on'; settings: DeliverySettings };

/**
 * Whether the mosque's store takes payment: GET /store/settings answers 404 `capability_disabled` while
 * `store_checkout` is off — then the delivery settings and the payment buttons are not shown.
 */
export function useCheckout() {
  const [c, setC] = useState<Checkout>({ state: 'loading' });
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    void send('/store/settings').then((r) => {
      if (!live) return;
      if (r.ok) setC({ state: 'on', settings: r.body as DeliverySettings });
      else if (r.status === 404 || r.status === 403) setC({ state: 'off' });
      else setC({ state: 'error', message: errText(r.body) });
    });
    return () => { live = false; };
  }, [n]);
  return { checkout: c, reload: () => setN((x) => x + 1), set: (s: DeliverySettings) => setC({ state: 'on', settings: s }) };
}

/* ------------------------------------------------------------------ the list */

type Order = Body;

export function Orders({ checkoutOn }: { checkoutOn: boolean }) {
  const orders = useLoad<Body>('/store/orders');
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState<string | null>(null);
  // One per view: a retry after the detail was closed and opened again is still the same attempt.
  const attempts = useRef(new Attempts());
  if (orders.error) return <LoadError error={orders.error} retry={orders.reload} />;
  if (!orders.data) return <Loading />;
  const all: Order[] = orders.data.items || [];
  const now = new Date();
  const list = all.filter((o) => inFilter(o.status, filter));
  const count = (id: string) => all.filter((o) => inFilter(o.status, id)).length;
  const urgent = all.filter((o) => handoverDue(o as any, now)).length;
  const show = (id: string) => {
    setOpen(open === id ? null : id);
  };
  return (
    <>
      <Card title="Ordrer" hint={checkoutOn
        ? 'Pengene er bare reservert til ordren er utlevert eller sendt. Da trekkes de.'
        : 'Butikken tar ikke imot betaling. Ordrer som allerede er betalt, kan fortsatt utleveres, frigis eller refunderes her.'}>
        {urgent > 0 && (
          <p className="msg warn" role="status" style={{ margin: '6px 18px' }}>
            <b>{urgent === 1 ? 'Én betalt ordre' : `${urgent} betalte ordrer`} må utleveres</b> – en betalt ordre frigis automatisk etter {AUTO_RELEASE_DAYS} dager, og kunden belastes ikke.
          </p>
        )}
        {all.length > 0 && (
          <div className="bnr-seg shop-filter" role="group" aria-label="Vis ordrer">
            {ORDER_FILTERS.map((f) => (
              <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>{f.label} <span className="shop-count">{count(f.id)}</span></button>
            ))}
          </div>
        )}
        {list.length > 0 && (
          <table className="t" aria-label="Ordrer, nyeste først">
            <thead><tr>
              <th scope="col">Ordre</th><th scope="col">Dato</th><th scope="col">Kjøper</th><th scope="col">Levering</th>
              <th scope="col">Sum</th><th scope="col">Status</th><th scope="col"><span className="vh">Vis</span></th>
            </tr></thead>
            <tbody>{list.map((o) => {
              const [word, tone] = statusWord(o.status, o.delivery);
              const due = handoverDue(o as any, now);
              return (
                <tr key={o.id} className={[due ? 'over' : '', open === o.id ? 'sel' : ''].filter(Boolean).join(' ') || undefined}>
                  <td>{o.orderNo || (o.source === 'staff' ? 'utkast' : '–')}</td>
                  <td>{osloTime(o.createdAt)}</td>
                  <td>{o.contact && o.contact.name ? o.contact.name : <span className="note">{o.source === 'staff' ? 'laget av ansatt' : '–'}</span>}</td>
                  <td>{DELIVERY_NO[o.delivery] || '–'}</td>
                  <td>{money(o.totalOre)}</td>
                  <td>
                    <Tag tone={tone}>{word}</Tag>
                    {due && <><br /><span className="msg warn" style={{ margin: 0 }}>Må utleveres – frigis automatisk etter {AUTO_RELEASE_DAYS} dager</span></>}
                  </td>
                  <td><Btn ghost aria-expanded={open === o.id} aria-controls="ordre-detalj" aria-label={`${open === o.id ? 'Skjul' : 'Vis'} ordre ${o.orderNo || ''}`.trim()}
                           onClick={() => show(o.id)}>{open === o.id ? 'Skjul' : 'Vis'}</Btn></td>
                </tr>);
            })}</tbody>
          </table>
        )}
        {!all.length && <p className="field note" style={{ margin: '10px 18px' }}>Ingen ordrer ennå.</p>}
        {all.length > 0 && !list.length && <p className="field note" style={{ margin: '10px 18px' }}>Ingen ordrer med denne statusen.</p>}
      </Card>
      {open && <OrderDetail key={open} id={open} attempts={attempts.current} onChanged={orders.reload} onClose={() => setOpen(null)} />}
    </>
  );
}

/* ------------------------------------------------------------------ one order */

type Asking = { kind: OpKind } | null;

function OrderDetail({ id, attempts, onChanged, onClose }: { id: string; attempts: Attempts; onChanged: () => void; onClose: () => void }) {
  const { data: o, error, reload } = useLoad<Body>(`/store/orders/${encodeURIComponent(id)}`, [id]);
  const [asking, setAsking] = useState<Asking>(null);
  // Shop shipping (2026-10-31): the «Send» dialog (carrier, tracking number), and the label through the shipping account.
  const [shipping, setShipping] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);
  const [msg, say] = useMsg();
  // When the order has loaded, focus goes to its heading: the detail is drawn below the list, out of sight.
  const focused = useRef(false);
  useEffect(() => {
    if (o && !focused.current) { focused.current = true; requestAnimationFrame(() => document.getElementById('ordre-detalj')?.focus()); }
  }, [o]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!o) return <Card><Loading /></Card>;
  const [word, tone] = statusWord(o.status, o.delivery);
  const due = handoverDue(o as any, new Date());
  const pay = o.payment;
  const can = o.can || {};
  const lines: Body[] = Array.isArray(o.lines) ? o.lines : [];
  const goods = lines.reduce((s, l) => s + l.unitPriceOre * l.quantity, 0);
  const handLabel = o.delivery === 'ship' ? 'Sendt' : 'Utlevert';
  const sh: OrderShipping | null = o.delivery === 'ship' && o.shipping ? (o.shipping as OrderShipping) : null;
  const makeLabel = async () => {
    if (labelBusy || !sh) return;
    setLabelBusy(true);
    const r = await post(`/store/orders/${encodeURIComponent(o.id)}/label`, sh.carrier && sh.carrier !== 'other' ? { carrier: sh.carrier } : {});
    setLabelBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    say(`Fraktetiketten er laget${r.body.shipping && r.body.shipping.trackingNumber ? ` – sporingsnummer ${r.body.shipping.trackingNumber}` : ''}. Last den ned og fest den på pakken, og trykk «Sendt» når pakken er levert inn.`, 'ok');
    reload(); onChanged();
  };
  const fetchLabel = async () => {
    const d = await download(`/v1/admin/store/orders/${encodeURIComponent(o.id)}/label`, `fraktetikett-ordre-${o.orderNo || 'x'}.pdf`);
    if (!d.ok) say(d.message || 'Kunne ikke hente etiketten.', 'err');
  };
  const dropLabel = async () => {
    const r = await send(`/store/orders/${encodeURIComponent(o.id)}/label`, { method: 'DELETE' });
    if (!r.ok) return say(errText(r.body), 'err');
    say('Etiketten er slettet. Sporingsnummeret står fortsatt på ordren.', 'ok');
    reload(); onChanged();
  };
  const done = (kind: OpKind, status: number, body: Body) => {
    const r = outcomeText(kind, status, body, o.delivery);
    say(r.text, r.tone);
    // A final answer, or one the server will finish itself: the question closes, and the order is read again.
    if (status !== 0 && status < 500) setAsking(null);
    reload(); onChanged();
  };
  return (
    <Card className="ordre-detalj">
      <h3 id="ordre-detalj" tabIndex={-1}>Ordre {o.orderNo || 'utkast'} <Tag tone={tone}>{word}</Tag></h3>
      <div className="card-body">
        {due && (
          <p className="msg warn" role="status" style={{ marginTop: 0 }}>
            <b>Må utleveres – frigis automatisk etter {AUTO_RELEASE_DAYS} dager.</b> Betalt for {due.days} dager siden; reservasjonen frigis {osloTime(due.releaseAt.toISOString())} hvis ordren ikke er utlevert.
          </p>
        )}
        {NOTICE_NO[o.notice] && <p className="msg warn" role="status" style={{ marginTop: 0 }}>{NOTICE_NO[o.notice]}</p>}
        <dl className="ordre-fakta">
          <dt>Bestilt</dt><dd>{osloTime(o.createdAt)}</dd>
          {o.delivery && <><dt>Levering</dt><dd>{DELIVERY_NO[o.delivery]}</dd></>}
          {o.fulfilledAt && <><dt>{handLabel}</dt><dd>{osloTime(o.fulfilledAt)}</dd></>}
          {sh && (sh.carrier || sh.shippedAt) && <>
            <dt>Sendes med</dt><dd>{sh.carrier ? CARRIER_NO[sh.carrier] || sh.carrier : 'Ikke valgt'}{sh.shippingMethod ? <span className="note"> · {sh.shippingMethod}</span> : null}</dd>
            {sh.shippedAt && <><dt>Pakken sendt</dt><dd>{osloTime(sh.shippedAt)}</dd></>}
            {sh.trackingNumber && <><dt>Sporing</dt><dd>{sh.trackingUrl
              ? <a href={sh.trackingUrl} target="_blank" rel="noopener noreferrer">{sh.trackingNumber}</a> : <code>{sh.trackingNumber}</code>}</dd></>}
            {sh.pickupPoint && <><dt>Hentested</dt><dd>{sh.pickupPoint}</dd></>}
          </>}
          {sh && <><dt>Vekt</dt><dd>{weightText(sh.weightG)} <span className="note">(varer uten vekt regnes som 500 g)</span></dd></>}
          {o.cancelledAt && <><dt>Kansellert</dt><dd>{osloTime(o.cancelledAt)}</dd></>}
          {o.contact && <>
            <dt>Kjøper</dt><dd>{o.contact.name}</dd>
            {o.contact.phone && <><dt>Telefon</dt><dd><a href={`tel:${o.contact.phone}`}>{o.contact.phone}</a></dd></>}
            {o.delivery === 'ship' && (o.contact.street || o.contact.city) && <>
              <dt>Sendes til</dt><dd>{o.contact.street}<br />{[o.contact.postcode, o.contact.city].filter(Boolean).join(' ')}</dd>
            </>}
          </>}
          {o.source === 'staff' && <><dt>Kjøper</dt><dd>Ingen – et ordreutkast laget av en ansatt, uten betaling.</dd></>}
        </dl>
      </div>
      <table className="t" aria-label="Varer i ordren">
        <thead><tr><th scope="col">Vare (slik den var bestilt)</th><th scope="col">Pris per stk.</th><th scope="col">Antall</th><th scope="col">Sum</th></tr></thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.productId}><td>{l.title}</td><td>{money(l.unitPriceOre)}</td><td>{l.quantity}</td><td>{money(l.unitPriceOre * l.quantity)}</td></tr>))}
          {o.shippingOre > 0 && <tr><td>Frakt</td><td></td><td></td><td>{money(o.shippingOre)}</td></tr>}
          <tr><th scope="row">Totalt</th><td></td><td></td><td><b>{money(o.totalOre ?? goods + (o.shippingOre || 0))}</b></td></tr>
        </tbody>
      </table>
      <div className="card-body">
        {pay && (
          <>
            <h4 style={{ margin: '4px 0 6px' }}>Betaling (Vipps)</h4>
            <dl className="ordre-fakta">
              <dt>Status</dt><dd>{PAY_NO[pay.status] || pay.status}</dd>
              <dt>Reservert</dt><dd>{money(pay.authorizedOre || 0)}</dd>
              <dt>Trukket</dt><dd>{money(pay.capturedOre || 0)}</dd>
              {pay.cancelledOre > 0 && <><dt>Frigitt</dt><dd>{money(pay.cancelledOre)}</dd></>}
              <dt>Refundert</dt><dd>{money(pay.refundedOre || 0)}</dd>
              <dt>Vipps-referanse</dt><dd><code>{pay.reference}</code></dd>
              {pay.lastCheckedAt && <><dt>Sist sjekket</dt><dd>{osloTime(pay.lastCheckedAt)}</dd></>}
            </dl>
          </>
        )}
        {Array.isArray(o.operations) && o.operations.length > 0 && (
          <>
            <h4 style={{ margin: '12px 0 6px' }}>Historikk</h4>
            <ol className="ordre-hist">{o.operations.map((op: Body) => (
              <li key={op.id}>{osloTime(op.doneAt || op.requestedAt)} – {operationWord(op as any, o.delivery)}</li>))}
            </ol>
          </>
        )}
        <Msg text={msg.text} tone={msg.tone} />
        {!asking && (can.handOver || can.cancel || can.refund || can.ship || can.label) && (
          <p className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* A shipped order: «Sendt» opens the dialog (carrier, tracking number), and the money is taken through it. */}
            {can.handOver && <Btn onClick={() => { say(''); if (sh) setShipping(true); else setAsking({ kind: 'handover' }); }}>{handLabel}</Btn>}
            {sh && can.ship && !can.handOver && <Btn ghost onClick={() => { say(''); setShipping(true); }}>{sh.trackingNumber ? 'Endre sporing' : 'Legg til sporing'}</Btn>}
            {sh && can.label && !sh.label && <Btn ghost disabled={labelBusy} aria-busy={labelBusy || undefined} onClick={() => { say(''); void makeLabel(); }}>{labelBusy ? 'Lager etikett …' : 'Lag fraktetikett'}</Btn>}
            {sh && sh.label && <Btn ghost onClick={() => { say(''); void fetchLabel(); }}>Last ned fraktetikett</Btn>}
            {sh && sh.label && <TwoStep label="Slett etikett" confirmLabel="Klikk igjen for å slette etiketten" onConfirm={() => { void dropLabel(); }} />}
            {can.cancel && <Btn ghost onClick={() => { say(''); setAsking({ kind: 'cancel' }); }}>Kanseller og frigi</Btn>}
            {can.refund && <Btn ghost onClick={() => { say(''); setAsking({ kind: 'refund' }); }}>Refunder</Btn>}
          </p>
        )}
        {sh && (
          <FormDialog open={shipping} title={`Send ordre ${o.orderNo || ''}`.trim()} onClose={() => setShipping(false)}
            hint={can.handOver ? `Pengene trekkes når du trykker «Sendt»: ${money(Math.max(0, ((pay && pay.authorizedOre) || 0) - ((pay && pay.capturedOre) || 0) - ((pay && pay.cancelledOre) || 0)) || o.totalOre)}. Kunden får beskjed om at pakken er sendt.`
              : 'Pengene er allerede trukket. Her endrer du transportør og sporing; kunden ser det på ordren.'}>
            <ShipForm key={`${o.id}:${o.updatedAt}`} order={o} shipping={sh} attempts={attempts} capture={Boolean(can.handOver)}
                      onCancel={() => setShipping(false)}
                      onDone={(status, body, carrier) => { const r = shipOutcomeText(status, body, carrier); say(r.text, r.tone);
                        if (status !== 0 && status < 500) setShipping(false); reload(); onChanged(); }} />
          </FormDialog>
        )}
        {asking && (
          <Confirm key={asking.kind} kind={asking.kind} order={o} attempts={attempts} handLabel={handLabel}
                   onCancel={() => setAsking(null)} onDone={done} />
        )}
        {o.source === 'app' && !can.handOver && !can.cancel && !can.refund && o.status === 'paid' && (
          <p className="field note">En handling på ordren venter på svar fra Vipps. Last inn på nytt om litt.</p>
        )}
        <p className="actions"><Btn ghost onClick={onClose}>Lukk ordren</Btn></p>
      </div>
    </Card>
  );
}

/**
 * «Send» (shop shipping, 2026-10-31): the carrier (prefilled with the buyer's choice), the tracking number staff
 * paste (the tracking page is built per carrier — for «Annen frakt» a link may be pasted), an optional pickup point.
 * Sent to POST …/ship with the SAME request id as a handover (Attempts), so a double click or «Prøv igjen» after no
 * answer is the same capture, never a second one.
 */
function ShipForm({ order, shipping, attempts, capture, onCancel, onDone }: {
  order: Body; shipping: OrderShipping; attempts: Attempts; capture: boolean; onCancel: () => void;
  onDone: (status: number, body: Body, carrier: string) => void;
}) {
  // Only carriers offered now (Posten paused 2026-10-31); an order that already has a paused one keeps it as a choice.
  const choices = Shipping.OFFERED.concat(shipping.carrier && !Shipping.isOffered(shipping.carrier) ? [shipping.carrier] : []);
  const [carrier, setCarrier] = useState<Carrier>(shipping.carrier || choices[0]!);
  const [nr, setNr] = useState(shipping.trackingNumber || '');
  const [url, setUrl] = useState(shipping.carrier === 'other' && shipping.trackingUrl ? shipping.trackingUrl : '');
  const [point, setPoint] = useState(shipping.pickupPoint || '');
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState<string[]>([]);
  const [lastFailed, setLastFailed] = useState(false);
  const [msg, say] = useMsg();
  const preview = carrier !== 'other' && Shipping.validTrackingNumber(nr) ? Shipping.trackingUrl(carrier, nr) : null;
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const v = Shipping.shipInput({ carrier, trackingNumber: nr, trackingUrl: carrier === 'other' ? url : undefined, pickupPoint: point });
    if (!v.ok) { setBad(Object.keys(v.errors)); return say(Object.values(v.errors)[0] || 'Sjekk feltene.', 'err'); }
    setBad([]);
    const key = Attempts.key(order.id, 'handover', null);
    const body: Body = { requestId: attempts.idFor(key), carrier, trackingNumber: nr.trim() || null, trackingUrl: carrier === 'other' ? url.trim() || null : null, pickupPoint: point.trim() || null };
    setBusy(true);
    const r = await post(`/store/orders/${encodeURIComponent(order.id)}/ship`, body);
    attempts.settle(key, r.status);
    setBusy(false);
    setLastFailed(r.status === 0 || r.status >= 500);
    onDone(r.status, r.body, carrier);
  };
  const inv = (k: string) => bad.includes(k) || undefined;
  return (
    <form onSubmit={go} aria-label="Send ordren" noValidate>
      <Field label="Transportør" required className="field half">
        {(id) => (
          <select id={id} value={carrier} disabled={busy || shipping.label} onChange={(e) => { setCarrier(e.target.value as Carrier); setBad([]); }}>
            {choices.map((c) => <option key={c} value={c}>{CARRIER_NO[c]}</option>)}
          </select>
        )}
      </Field>
      <Field label="Sporingsnummer" className="field half" note={shipping.label ? 'Fra etiketten; kan ikke endres mens etiketten finnes.'
        : preview ? `Kunden får lenken ${preview}` : carrier === 'other' ? 'Valgfritt. 8–40 bokstaver og sifre.' : 'Valgfritt, men kunden kan bare følge pakken med det. 8–40 bokstaver og sifre, slik det står på kvitteringen.'}>
        {(id, d) => <input id={id} autoComplete="off" aria-describedby={d} aria-invalid={inv('trackingNumber')} value={nr} disabled={busy || shipping.label}
                           onChange={(e) => { setNr(e.target.value); setBad([]); setLastFailed(false); }} />}
      </Field>
      {carrier === 'other' && (
        <Field label="Sporingslenke" className="field" note="Valgfri. En https-adresse kunden kan åpne for å følge pakken.">
          {(id, d) => <input id={id} type="url" autoComplete="off" aria-describedby={d} aria-invalid={inv('trackingUrl')} value={url} disabled={busy || shipping.label}
                             onChange={(e) => { setUrl(e.target.value); setBad([]); }} />}
        </Field>
      )}
      <Field label="Hentested" className="field" note="Valgfritt. For eksempel «Rema 1000 Grønland» når pakken hentes i butikk.">
        {(id, d) => <input id={id} maxLength={Shipping.MAX_PICKUP_POINT} autoComplete="off" aria-describedby={d} aria-invalid={inv('pickupPoint')} value={point} disabled={busy}
                           onChange={(e) => { setPoint(e.target.value); setBad([]); }} />}
      </Field>
      <Msg text={msg.text} tone={msg.tone} />
      <p className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 0 }}>
        <Btn type="submit" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Sender …' : lastFailed ? 'Prøv igjen' : capture ? 'Sendt – trekk pengene' : 'Lagre sporing'}</Btn>
        <Btn type="button" ghost disabled={busy} onClick={onCancel}>Avbryt</Btn>
      </p>
    </form>
  );
}

/**
 * The question before money moves: what happens, how much. For a refund, how much (kroner with øre; at most what
 * can be refunded). Focus goes to the question; «Avbryt» or Escape closes it.
 */
function Confirm({ kind, order, attempts, handLabel, onCancel, onDone }: {
  kind: OpKind; order: Body; attempts: Attempts; handLabel: string; onCancel: () => void; onDone: (kind: OpKind, status: number, body: Body) => void;
}) {
  const max: number = (order.can && order.can.refundableOre) || 0;
  const [amount, setAmount] = useState(() => krText(max));
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState('');
  const [lastFailed, setLastFailed] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { requestAnimationFrame(() => box.current?.querySelector<HTMLElement>('input, button.btn:not(.ghost)')?.focus()); }, []);
  const pay = order.payment || {};
  const reserved = Math.max(0, (pay.authorizedOre || 0) - (pay.capturedOre || 0) - (pay.cancelledOre || 0));
  const parsed = kind === 'refund' ? parseKr(amount, { min: 1, max, what: 'Beløpet' }) : null;
  const question = kind === 'handover' ? `Pengene trekkes nå: ${money(reserved || order.totalOre)}.`
    : kind === 'cancel' ? (reserved > 0 ? `Reservasjonen på ${money(reserved)} frigis. Kunden belastes ikke, og varene legges tilbake på lager.`
      : 'Ordren kanselleres før den er betalt. Varene legges tilbake på lager.')
    : `Hvor mye skal refunderes? Høyst ${money(max)}.`;
  const go = async () => {
    if (busy) return;
    if (parsed && !parsed.ok) { setBad(parsed.message); return; }
    const amountOre = parsed && parsed.ok ? parsed.ore : null;
    const key = Attempts.key(order.id, kind, amountOre);
    const body: Body = { requestId: attempts.idFor(key) };
    if (kind === 'refund') body.amountOre = amountOre;
    setBusy(true);
    const r = await post(`/store/orders/${encodeURIComponent(order.id)}/${kind}`, body);
    attempts.settle(key, r.status);
    setBusy(false);
    setLastFailed(r.status === 0 || r.status >= 500);
    onDone(kind, r.status, r.body);
  };
  const confirmLabel = busy ? 'Sender …' : lastFailed ? 'Prøv igjen'
    : kind === 'handover' ? `${handLabel} – trekk ${money(reserved || order.totalOre)}`
    : kind === 'cancel' ? 'Kanseller og frigi'
    : parsed && parsed.ok ? `Refunder ${money(parsed.ore)}` : 'Refunder';
  return (
    <div ref={box} className="ordre-confirm" role="group" aria-labelledby="ordre-confirm-q"
         onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onCancel(); }}>
      <p id="ordre-confirm-q" style={{ marginTop: 0 }}><b>{question}</b></p>
      {kind === 'refund' && (
        <Field label="Beløp (kr)" required className="field" note="Kroner med øre, for eksempel 149 eller 149,50.">
          {(fid, d) => <input id={fid} inputMode="decimal" autoComplete="off" aria-describedby={d} aria-invalid={bad ? true : undefined}
                              value={amount} disabled={busy} onChange={(e) => { setAmount(e.target.value); setBad(''); setLastFailed(false); }} />}
        </Field>
      )}
      {bad && <p className="msg err" role="alert">{bad}</p>}
      <p className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 0 }}>
        <Btn danger={kind !== 'handover'} disabled={busy} aria-busy={busy || undefined} onClick={go}>{confirmLabel}</Btn>
        <Btn ghost disabled={busy} onClick={onCancel}>Avbryt</Btn>
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ the seller (audit F-04) */

const SELLER_LABELS = { sellerName: SELLER_NO.name, sellerOrgNo: SELLER_NO.orgNo, sellerAddress: SELLER_NO.address, sellerEmail: SELLER_NO.email, sellerPhone: SELLER_NO.phone };

/**
 * The seller's details: shown to every buyer in the terms before «Betal med Vipps» (who sells, org. no., where to
 * write, the 14-day right to cancel). Sent with the delivery options as they are stored, so saving one never changes
 * the other.
 */
export function SellerForm({ settings, onSaved }: { settings: DeliverySettings; onSaved: (s: DeliverySettings) => void }) {
  const start = sellerFields(settings.seller);
  const [f, setF] = useState(start);
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState<string[]>([]);
  const dirty = sellerDirty(f, start);
  useDirty('butikk-selger', dirty);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const r = await send('/store/settings', { method: 'PUT', body: JSON.stringify({
      pickup: settings.pickup, shipping: settings.shipping, shippingFeeOre: settings.shippingFeeOre || 0, pickupNote: settings.pickupNote,
      seller: { name: f.name, orgNo: f.orgNo, address: f.address, email: f.email, phone: f.phone } }) });
    setBusy(false);
    if (!r.ok) { setBad(Object.keys((r.body && r.body.errors) || {})); return say(errText(r.body, SELLER_LABELS), 'err'); }
    setBad([]);
    const saved = r.body as DeliverySettings;
    // The server normalises what it stores («123 456 789» → «123456789», a trimmed address): the form now shows what is
    // stored, so it is no longer «unsaved» against it and «Lagre selger» goes grey (review 2026-10-19).
    setF(sellerFields(saved.seller));
    say(saved.sellerMissing && saved.sellerMissing.length ? 'Lagret. Butikken er fortsatt stengt til alle feltene med * er fylt inn.'
      : 'Selgeropplysningene er lagret. Kundene ser dem i salgsvilkårene før de betaler.', 'ok');
    onSaved(saved);
  };
  const input = (k: keyof SellerFields, err: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (id: string, d?: string) => (
    <input id={id} aria-describedby={d} aria-invalid={bad.includes(err) || undefined} value={f[k]} disabled={busy}
           onChange={(e) => setF({ ...f, [k]: e.target.value })} {...extra} />);
  return (
    <form onSubmit={save} aria-label="Selger" noValidate>
      <Field label={SELLER_NO.name} required className="field" note="Slik det står i Enhetsregisteret, f.eks. «Moskeen Eksempel».">
        {input('name', 'sellerName', { maxLength: 120, autoComplete: 'organization' })}
      </Field>
      <Field label={SELLER_NO.orgNo} required className="field half" note="Ni siffer.">
        {input('orgNo', 'sellerOrgNo', { inputMode: 'numeric', maxLength: 11, autoComplete: 'off' })}
      </Field>
      <Field label={SELLER_NO.address} required className="field" note="Gate, postnummer og sted – der kjøpere kan sende brev og returnere varer.">
        {input('address', 'sellerAddress', { maxLength: 200, autoComplete: 'street-address' })}
      </Field>
      <Field label={SELLER_NO.email} required className="field half" note="Hit sender kjøpere spørsmål, angrer og klager.">
        {input('email', 'sellerEmail', { type: 'email', maxLength: 120, autoComplete: 'email' })}
      </Field>
      <Field label={SELLER_NO.phone} className="field half" note="Valgfri. Norsk nummer, 8 siffer.">
        {input('phone', 'sellerPhone', { inputMode: 'tel', maxLength: 20, autoComplete: 'tel' })}
      </Field>
      <Msg text={msg.text} tone={msg.tone} />
      <p className="actions"><Btn type="submit" disabled={busy || !dirty} aria-busy={busy || undefined}>{busy ? 'Lagrer …' : 'Lagre selger'}</Btn></p>
    </form>
  );
}

/* ------------------------------------------------------------------ delivery settings */

const SETTING_LABELS = { pickup: 'Henting', shipping: 'Sending', shippingFeeOre: 'Frakt', pickupNote: 'Henteinformasjon', shippingMode: 'Fraktmåte', shippingCarriers: 'Transportører' };
const MAX_FEE_ORE = 200_000;
const MAX_NOTE = 300;

const deliveryFields = (d: DeliverySettings) => ({ pickup: d.pickup, shipping: d.shipping, fee: krText(d.shippingFeeOre || 0), note: d.pickupNote || '',
  mode: (d.shippingMode || 'flat') as 'flat' | 'carrier', carriers: [...(d.shippingCarriers || [])] as Carrier[] });

/**
 * Delivery: pickup (with its note) and shipping — as one flat fee, or (shop shipping, 2026-10-31) with a carrier,
 * priced per carrier and weight from the rate table below (RatesForm).
 */
export function DeliverySettingsForm({ settings, onSaved }: { settings: DeliverySettings; onSaved: (s: DeliverySettings) => void }) {
  const start = deliveryFields(settings);
  const [f, setF] = useState(start);
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const dirty = f.pickup !== start.pickup || f.shipping !== start.shipping || f.fee !== start.fee || f.note.trim() !== start.note.trim()
    || f.mode !== start.mode || f.carriers.join() !== start.carriers.join();
  useDirty('butikk-levering', dirty);
  const fee = parseKr(f.fee, { max: MAX_FEE_ORE, what: 'Frakten' });
  const byCarrier = f.shipping && f.mode === 'carrier';
  const toggleCarrier = (c: Carrier, on: boolean) => setF({ ...f, carriers: Shipping.OFFERED.filter((x) => (x === c ? on : f.carriers.includes(x))) });
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.pickup && !f.shipping) return say('Varer må kunne hentes, sendes eller begge deler.', 'err');
    if (f.shipping && f.mode === 'flat' && !fee.ok) return say(fee.message, 'err');
    if (byCarrier && !f.carriers.length) return say('Velg minst én transportør.', 'err');
    setBusy(true);
    const r = await send('/store/settings', { method: 'PUT', body: JSON.stringify({
      pickup: f.pickup, shipping: f.shipping, shippingFeeOre: f.shipping && f.mode === 'flat' && fee.ok ? fee.ore : (settings.shippingFeeOre || 0),
      pickupNote: f.note.trim() ? f.note.trim() : null, shippingMode: f.mode, shippingCarriers: f.carriers }) });
    setBusy(false);
    if (!r.ok) return say(errText(r.body, SETTING_LABELS), 'err');
    setF(deliveryFields(r.body as DeliverySettings));   // «49,5» is stored as 49,50: show what is stored, not «unsaved»
    const saved = r.body as DeliverySettings;
    say(saved.shippingMode === 'carrier' && !(saved.rates || []).some((x) => saved.shippingCarriers?.includes(x.carrier))
      ? 'Leveringen er lagret. Legg inn fraktsatser under – uten satser kan ingen ordre sendes.'
      : 'Leveringen er lagret. Kundene ser endringen neste gang de går til kassen.', 'ok');
    onSaved(saved);
  };
  return (
    <form onSubmit={save} aria-label="Levering" noValidate>
      <label className="check"><input type="checkbox" checked={f.pickup} disabled={busy} onChange={(e) => setF({ ...f, pickup: e.target.checked })} /> Kan hentes i moskeen</label>
      {f.pickup && (
        <Field label="Henteinformasjon" className="field" note={`Valgfri. Vises for kunden ved henting, f.eks. når og hvor. ${f.note.trim().length} av høyst ${MAX_NOTE} tegn.`}>
          {(id, d) => <textarea id={id} rows={2} maxLength={MAX_NOTE} aria-describedby={d} value={f.note} disabled={busy} onChange={(e) => setF({ ...f, note: e.target.value })} />}
        </Field>
      )}
      <label className="check"><input type="checkbox" checked={f.shipping} disabled={busy} onChange={(e) => setF({ ...f, shipping: e.target.checked })} /> Kan sendes</label>
      {f.shipping && (
        <fieldset className="field" style={{ border: 0, padding: 0, margin: '6px 0 0' }}>
          <legend className="note" style={{ padding: 0, marginBottom: 4 }}>Fraktmåte</legend>
          {(['flat', 'carrier'] as const).map((m) => (
            <label key={m} className="check"><input type="radio" name="fraktmaate" value={m} checked={f.mode === m} disabled={busy} onChange={() => setF({ ...f, mode: m })} /> {SHIPPING_MODE_NO[m]}</label>
          ))}
        </fieldset>
      )}
      {f.shipping && f.mode === 'flat' && (
        <Field label="Frakt (kr)" required className="field half" note="Legges til hver ordre som sendes. 0 for fri frakt; høyst 2 000 kr.">
          {(id, d) => <input id={id} inputMode="decimal" autoComplete="off" aria-describedby={d} aria-invalid={!fee.ok || undefined}
                             value={f.fee} disabled={busy} onChange={(e) => setF({ ...f, fee: e.target.value })} />}
        </Field>
      )}
      {byCarrier && (
        <fieldset className="field" style={{ border: 0, padding: 0, margin: '6px 0 0' }}>
          <legend className="note" style={{ padding: 0, marginBottom: 4 }}>Transportører kunden kan velge mellom. Prisen per transportør og vekt settes i fraktsatsene under.</legend>
          {Shipping.OFFERED.map((c) => (
            <label key={c} className="check"><input type="checkbox" checked={f.carriers.includes(c)} disabled={busy} onChange={(e) => toggleCarrier(c, e.target.checked)} /> {CARRIER_NO[c]}</label>
          ))}
        </fieldset>
      )}
      <Msg text={msg.text} tone={msg.tone} />
      <p className="actions"><Btn type="submit" disabled={busy || !dirty} aria-busy={busy || undefined}>{busy ? 'Lagrer …' : 'Lagre levering'}</Btn></p>
    </form>
  );
}

/* ------------------------------------------------------------------ the rate table (shop shipping, 2026-10-31) */

/**
 * «Posten inntil 2 kg: 99 kr»: one row per carrier and weight band. The checkout picks, per carrier the mosque uses,
 * the smallest band that takes the basket's weight; a basket no band takes cannot be sent with that carrier. The whole
 * table is saved at once (PUT /store/shipping/rates), checked by core shipping.rates.
 */
export function RatesForm({ settings, onSaved }: { settings: DeliverySettings; onSaved: (s: DeliverySettings) => void }) {
  const stored = settings.rates || [];
  const [rows, setRows] = useState<RateRow[]>(() => stored.map(rateRow));
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const dirty = ratesDirty(rows, stored);
  useDirty('butikk-fraktsatser', dirty);
  const enabled = settings.shippingCarriers || [];
  const set = (i: number, patch: Partial<RateRow>) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const add = () => setRows([...rows, { carrier: enabled[0] || Shipping.OFFERED[0]!, maxKg: '', price: '' }]);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = parseRates(rows);
    if (!v.ok) return say(v.message, 'err');
    setBusy(true);
    const r = await send('/store/shipping/rates', { method: 'PUT', body: JSON.stringify({ items: v.items }) });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    const items = (r.body.items || []) as ShippingRate[];
    setRows(items.map(rateRow));
    const missing = enabled.filter((c) => !items.some((x) => x.carrier === c));
    say(missing.length ? `Fraktsatsene er lagret. ${missing.map((c) => CARRIER_NO[c]).join(', ')} har ingen sats ennå og kan ikke velges i kassen.`
      : 'Fraktsatsene er lagret. Kundene ser prisene neste gang de går til kassen.', 'ok');
    onSaved({ ...settings, rates: items });
  };
  return (
    <form onSubmit={save} aria-label="Fraktsatser" noValidate>
      {rows.length > 0 && (
        <table className="t" aria-label="Fraktsatser per transportør og vekt">
          <thead><tr><th scope="col">Transportør</th><th scope="col">Inntil (kg)</th><th scope="col">Pris (kr)</th><th scope="col"><span className="vh">Fjern</span></th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i}>
              <td><select aria-label={`Rad ${i + 1}: transportør`} value={r.carrier} disabled={busy} onChange={(e) => set(i, { carrier: e.target.value as Carrier })}>
                {Shipping.OFFERED.map((c) => <option key={c} value={c}>{CARRIER_NO[c]}{enabled.includes(c) ? '' : ' (ikke slått på)'}</option>)}
              </select></td>
              <td><input aria-label={`Rad ${i + 1}: inntil kilo`} inputMode="decimal" autoComplete="off" value={r.maxKg} disabled={busy} style={{ width: '6em' }} onChange={(e) => set(i, { maxKg: e.target.value })} /></td>
              <td><input aria-label={`Rad ${i + 1}: pris i kroner`} inputMode="decimal" autoComplete="off" value={r.price} disabled={busy} style={{ width: '7em' }} onChange={(e) => set(i, { price: e.target.value })} /></td>
              <td><Btn ghost disabled={busy} aria-label={`Fjern rad ${i + 1}`} onClick={() => setRows(rows.filter((_, k) => k !== i))}>Fjern</Btn></td>
            </tr>))}
          </tbody>
        </table>
      )}
      {!rows.length && <p className="field note" style={{ margin: '6px 0' }}>Ingen fraktsatser ennå. Uten satser kan ingen ordre sendes med transportør.</p>}
      <Msg text={msg.text} tone={msg.tone} />
      <p className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn ghost disabled={busy || rows.length >= Shipping.MAX_RATES} onClick={add}>+ Ny sats</Btn>
        <Btn type="submit" disabled={busy || !dirty} aria-busy={busy || undefined}>{busy ? 'Lagrer …' : 'Lagre fraktsatser'}</Btn>
      </p>
    </form>
  );
}
